<template>
  <div class="chaizen-app">
    <!-- Custom Cursor -->
    <div class="cursor-follower" :style="cursorStyle"></div>
    
    <!-- Navigation -->
    <nav class="navbar navbar-expand-lg py-3 fixed-top glass-nav-light" :class="{ 'scrolled': scrolled }">
      <div class="container-fluid px-3 px-md-5">
        <div class="container d-flex align-items-center justify-content-between w-100">
          <a class="navbar-brand fw-bolder" href="#" @click.prevent="scrollToSection('home')">
            CHA<span class="text-gold">IZEN</span>
            <span class="brand-dot-light"></span>
          </a>
          <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav">
            <span class="navbar-toggler-icon"></span>
          </button>
          <div class="collapse navbar-collapse" id="navbarNav">
            <ul class="navbar-nav mx-auto mb-2 mb-lg-0 gap-lg-4">
              <li class="nav-item" v-for="item in navItems" :key="item">
                <a class="nav-link-light" href="#" @click.prevent="scrollToSection(item.toLowerCase())">
                  {{ item }}
                  <span class="nav-hover-line-light"></span>
                </a>
              </li>
            </ul>
            <div class="d-flex">
              <a href="#" class="btn-chaizen-light rounded-pill px-4 py-2" @click.prevent="showInquiry = true">
                <i class="fas fa-comment-dots me-2"></i>Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section id="home" class="hero-section-light pt-5">
      <div class="hero-bg-animation-light">
        <div class="orb-light orb-1-light"></div>
        <div class="orb-light orb-2-light"></div>
        <div class="orb-light orb-3-light"></div>
      </div>
      <div class="container">
        <div class="row align-items-center justify-content-center min-vh-100 py-5">
          <div class="col-12 col-lg-5 order-2 order-lg-1 text-center text-lg-start" data-aos="fade-right">
            <div class="hero-badge-light mb-4 d-inline-flex">
              <span class="badge-glow-light">
                <i class="fas fa-star-of-life text-gold me-2"></i> Premium Tea Exporters & Packers
              </span>
            </div>
            <h1 class="hero-title-light mb-4">
              A Legacy of <span class="text-gradient-light">Kenyan Excellence</span>
              <span class="typing-cursor-light">|</span>
            </h1>
            <p class="hero-subtitle-light mb-4">Chaizen is a Kenyan tea company dedicated to sourcing, blending, and packing the finest teas from our nation's renowned highlands. Led by directors whose combined experience spans over 20 years in the tea industry, Chaizen ensures quality is served in various parts of the world.</p>
            <div class="d-flex flex-wrap gap-3 mb-5 justify-content-center justify-content-lg-start">
              <button class="btn-3d-light" @click="scrollToSection('about')">
                <span class="btn-3d-front-light">Learn More About Us</span>
                <i class="fas fa-arrow-right ms-2"></i>
              </button>
              <button class="btn-outline-premium-light" @click="scrollToSection('products')">
                View Our Products
                <i class="fas fa-play-circle ms-2"></i>
              </button>
            </div>
            <div class="stats-container-light">
              <div class="row g-3 justify-content-center">
                <div class="col-4" v-for="(stat, idx) in stats" :key="stat.label">
                  <div class="stat-card-light" :style="{ animationDelay: `${idx * 0.1}s` }">
                    <div class="stat-number-light">
                      <span class="counter" :data-target="stat.value">{{ stat.displayValue }}</span>
                      <span class="stat-suffix-light">{{ stat.suffix }}</span>
                    </div>
                    <p class="stat-label-light">{{ stat.label }}</p>
                    <div class="stat-shine-light"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-5 order-1 order-lg-2 text-center" data-aos="fade-left" data-aos-delay="200">
            <div class="hero-3d-card-light">
              <div class="hero-3d-inner-light">
                <img :src="heroImage" 
                     alt="Chaizen Premium Kenyan Tea" 
                     class="hero-3d-img-light"
                     @mouseenter="startTiltAnimation"
                     @mouseleave="resetTiltAnimation">
                <div class="glow-effect-light"></div>
              </div>
              <div class="floating-particles-light">
                <div class="particle-light" v-for="n in 10" :key="n" :style="{ animationDelay: `${n * 0.4}s`, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="scroll-indicator-light" @click="scrollToSection('stats')">
        <span class="scroll-text-light">Scroll</span>
        <div class="scroll-wheel-light"></div>
      </div>
    </section>

    <!-- Stats Section -->
    <section id="stats" class="stats-section-light py-5">
      <div class="container">
        <div class="row text-center g-4 justify-content-center">
          <div class="col-6 col-md-3" v-for="(stat, idx) in trustStats" :key="stat.label" 
               data-aos="zoom-in" :data-aos-delay="idx * 100">
            <div class="trust-card-light p-3">
              <div class="trust-icon-wrapper-light">
                <i :class="stat.icon + ' trust-icon-light'"></i>
                <div class="icon-ring-light"></div>
              </div>
              <h3 class="trust-number-light">
                <span class="counter" :data-target="stat.valueNum">{{ stat.displayNum }}</span>
                <span v-if="stat.suffix">{{ stat.suffix }}</span>
              </h3>
              <p class="trust-label-light">{{ stat.label }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- About Us Section -->
    <section id="about" class="about-section-light py-5">
      <div class="container">
        <div class="row align-items-center justify-content-center g-5">
          <div class="col-12 col-lg-5" data-aos="fade-right">
            <div class="about-image-stack-light text-center">
              <div class="image-main-light d-inline-block">
                <img src="https://placehold.co/500x450/e8d5bc/c8a45e?text=Kenyan+Tea+Legacy&font=playfair" 
                     alt="Kenyan Tea Heritage" 
                     class="about-img-light">
                <div class="image-glow-light"></div>
              </div>
              <div class="image-decoration-light"></div>
            </div>
          </div>
          <div class="col-12 col-lg-6" data-aos="fade-left" data-aos-delay="200">
            <div class="about-badge-light mb-3">
              <span class="badge-modern-light">
                <i class="fas fa-gem me-2 text-gold"></i> About Us
              </span>
            </div>
            <h2 class="display-5 fw-bold mb-4">A Legacy of <span class="text-gradient-light">Kenyan Excellence</span></h2>
            <p class="about-lead-light mb-3">Chaizen is a Kenyan tea brand focused on gathering, crafting, and boxing top-tier teas from our country's famous high-altitude regions. Our leadership team brings over 20 years of shared tea industry experience to the table.</p>
            <p class="about-text-light mb-4">By collaborating firsthand with elite tea growers and the Mombasa Tea Auction, we obtain unique selections, operating as a dependable gateway from Kenya's top tea roots to worldwide consumers.</p>
            <div class="mission-vision-grid mb-4">
              <div class="mission-card">
                <div class="mission-icon">
                  <i class="fas fa-bullseye"></i>
                </div>
                <h4>Our Mission</h4>
                <p>To bring joy to international tea drinkers through unfailingly superior Kenyan teas.</p>
              </div>
              <div class="vision-card">
                <div class="vision-icon">
                  <i class="fas fa-eye"></i>
                </div>
                <h4>Our Vision</h4>
                <p>To become a leading global supplier of premium Kenyan teas.</p>
              </div>
            </div>
            <div class="values-row">
              <div class="value-item" v-for="value in values" :key="value">
                <div class="value-icon">
                  <i class="fas fa-heart"></i>
                </div>
                <span>{{ value }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Products Section -->
    <section id="products" class="services-section-light py-5">
      <div class="container">
        <div class="section-header-light text-center mb-5" data-aos="fade-up">
          <div class="section-badge-light">
            <i class="fas fa-crown me-2 text-gold"></i> Our Products
          </div>
          <h2 class="display-5 fw-bold mt-3">Premium <span class="text-gradient-light">Tea Products</span></h2>
          <p class="col-md-8 mx-auto mt-3">Our comprehensive catalogue includes black teas, green teas, specialty purple teas, and value-added products — from bulk grades to retail-ready packs.</p>
          <div class="section-divider-light"></div>
        </div>

        <div class="row g-4 justify-content-center">
          <div class="col-md-6 col-lg-3" v-for="(product, idx) in teaProducts" :key="product.title" 
               data-aos="flip-left" :data-aos-delay="idx * 100">
            <div class="service-card-light" @mousemove="handleCardMouseMove" @mouseleave="handleCardMouseLeave">
              <div class="service-card-inner-light p-4">
                <div class="service-icon-wrapper-light mb-3">
                  <div class="service-icon-bg-light"></div>
                  <i :class="product.icon + ' service-icon-light'"></i>
                </div>
                <h4 class="service-title-light mb-2">{{ product.title }}</h4>
                <p class="service-desc-light">{{ product.description }}</p>
                <div class="service-shimmer-light"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center mt-5">
          <button class="btn-premium-light" @click="scrollToSection('contact')">
            View All Products
            <i class="fas fa-arrow-right ms-2"></i>
            <div class="btn-shine-light"></div>
          </button>
        </div>
      </div>
    </section>

    <!-- Image Carousel Section -->
    <section id="carousel" class="carousel-section-light py-5">
      <div class="container">
        <div class="section-header-light text-center mb-5" data-aos="fade-up">
          <div class="section-badge-light">
            <i class="fas fa-images me-2 text-gold"></i> Tea Collection
          </div>
          <h2 class="display-5 fw-bold mt-3">Explore Our <span class="text-gradient-light">Tea Range</span></h2>
          <p class="col-md-8 mx-auto mt-3">Browse through our premium tea collection featuring the finest Kenyan teas</p>
          <div class="section-divider-light"></div>
        </div>

        <div class="carousel-wrapper" data-aos="fade-up" data-aos-delay="100">
          <div class="carousel-container">
            <button class="carousel-btn prev-btn" @click="prevSlide">
              <i class="fas fa-chevron-left"></i>
            </button>
            
            <div class="carousel-track" :style="{ transform: `translateX(-${currentSlide * 33.33}%)` }">
              <div class="carousel-slide" v-for="(item, idx) in carouselItems" :key="idx">
                <div class="carousel-card" @mousemove="handleCardMouseMove" @mouseleave="handleCardMouseLeave">
                  <div class="carousel-card-inner">
                    <div class="carousel-image-wrapper">
                      <img :src="item.image" :alt="item.name" class="carousel-image">
                      <div class="carousel-image-overlay">
                        <i class="fas fa-search-plus"></i>
                      </div>
                    </div>
                    <div class="carousel-card-content">
                      <h4 class="carousel-product-name">{{ item.name }}</h4>
                      <p class="carousel-product-desc">{{ item.description }}</p>
                      <span class="carousel-product-tag">{{ item.tag }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button class="carousel-btn next-btn" @click="nextSlide">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <div class="carousel-dots">
            <span v-for="(_, idx) in carouselPages" :key="idx" 
                  :class="['carousel-dot', { active: currentSlide === idx }]"
                  @click="goToSlide(idx)"></span>
          </div>
        </div>
      </div>
    </section>

    <!-- Quality Assurance Section -->
    <section id="quality" class="quality-section-light py-5">
      <div class="container">
        <div class="row align-items-center justify-content-center g-5">
          <div class="col-12 col-lg-6 order-2 order-lg-1" data-aos="fade-right">
            <div class="quality-badge-light mb-3">
              <span class="badge-modern-light">
                <i class="fas fa-shield-alt me-2 text-gold"></i> Quality Assurance
              </span>
            </div>
            <h2 class="display-5 fw-bold mb-4">Uncompromising <span class="text-gradient-light">Quality</span></h2>
            <p class="about-lead-light mb-4">Every batch is cupped, graded, and verified to meet international food safety standards before it leaves our facility.</p>
            <div class="quality-features-grid">
              <div class="quality-feature" v-for="feature in qualityFeatures" :key="feature.title">
                <div class="quality-feature-icon">
                  <i :class="feature.icon"></i>
                </div>
                <div class="quality-feature-content">
                  <h4>{{ feature.title }}</h4>
                  <p>{{ feature.description }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-5 order-1 order-lg-2 text-center" data-aos="fade-left" data-aos-delay="200">
            <div class="about-image-stack-light text-center">
              <div class="image-main-light d-inline-block">
                <img src="https://placehold.co/500x450/e8d5bc/c8a45e?text=Quality+Assurance&font=playfair" 
                     alt="Quality Assurance" 
                     class="about-img-light">
                <div class="image-glow-light"></div>
              </div>
              <div class="image-decoration-light"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Global Presence -->
    <section id="global" class="global-section-light py-5 my-4">
      <div class="container">
        <div class="text-center mb-5" data-aos="fade-up">
          <h2 class="display-5 fw-bold">We export our products to <span class="text-gradient-light">overseas markets</span></h2>
          <div class="global-line-light"></div>
          <p class="col-md-8 mx-auto mt-3">We export variety of tea grades of different types to countries across the globe</p>
        </div>
        <div class="text-center" data-aos="zoom-in" data-aos-delay="100">
          <div class="d-flex flex-wrap justify-content-center gap-2">
            <span class="country-pill-light" v-for="country in exportCountries" :key="country.code">
              {{ country.flag }} {{ country.name }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section id="stories" class="testimonials-section-light py-5">
      <div class="container">
        <div class="section-header-light text-center mb-5" data-aos="fade-up">
          <i class="fas fa-quote-right fa-3x text-gold opacity-50 mb-3"></i>
          <h2 class="display-5 fw-bold">What Our <span class="text-gradient-light">Partners Say</span></h2>
          <p class="col-md-8 mx-auto">Trusted by tea merchants and distributors worldwide</p>
        </div>
        <div class="testimonials-slider-light" data-aos="fade-up" data-aos-delay="100">
          <div class="testimonial-track-light" :style="{ transform: `translateX(-${currentTestimonial * 33.33}%)` }">
            <div class="testimonial-card-light" v-for="testimonial in testimonials" :key="testimonial.name">
              <div class="testimonial-content-light p-4">
                <div class="quote-icon-light">
                  <i class="fas fa-quote-left"></i>
                </div>
                <p class="testimonial-text-light mb-3">{{ testimonial.quote }}</p>
                <div class="testimonial-author-light">
                  <div class="author-avatar-light">
                    <div class="avatar-inner-light"></div>
                  </div>
                  <div class="author-info-light">
                    <strong>— {{ testimonial.name }}</strong>
                    <small>{{ testimonial.title }}</small>
                  </div>
                </div>
                <div class="rating-light mt-2">
                  <i class="fas fa-star" v-for="n in 5" :key="n"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="slider-controls-light mt-4">
            <button class="slider-btn-light prev" @click="prevTestimonial">
              <i class="fas fa-chevron-left"></i>
            </button>
            <div class="slider-dots-light">
              <span v-for="(_, idx) in testimonials" :key="idx" 
                    :class="['dot-light', { active: currentTestimonial === idx }]"
                    @click="currentTestimonial = idx"></span>
            </div>
            <button class="slider-btn-light next" @click="nextTestimonial">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Banner -->
    <section class="cta-section-light my-5 py-4">
      <div class="container">
        <div class="cta-card-light p-4 text-center text-lg-start" data-aos="flip-up">
          <div class="row align-items-center">
            <div class="col-lg-8">
              <h3 class="fw-bold">Your gateway to <span class="text-gold">premium bulk Kenyan tea</span></h3>
              <p class="mb-0 mt-2 opacity-75">We work directly with top tea gardens and the Mombasa Tea Auction to secure exceptional lots. Contact us today to bring the best Kenyan tea to your market!</p>
            </div>
            <div class="col-lg-4 text-lg-end mt-3 mt-lg-0">
              <button class="btn-cta-light" @click="showInquiry = true">
                Request Free Samples
                <i class="fas fa-gift ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Industry Affiliations -->
    <div class="container text-center mb-5 pb-2">
      <p class="text-uppercase small text-muted tracking-wide">Our Industry Affiliations</p>
      <div class="d-flex flex-wrap justify-content-center gap-2 align-items-center">
        <span class="affiliation-pill" v-for="affiliation in affiliations" :key="affiliation">
          {{ affiliation }}
        </span>
      </div>
    </div>

    <!-- Footer -->
    <footer class="footer-light pt-5 pb-4">
      <div class="container">
        <div class="row g-4">
          <div class="col-md-5 text-center text-md-start">
            <h3 class="footer-logo-light mb-3">CHA<span class="text-gold">IZEN</span></h3>
            <p class="footer-desc-light">Chaizen is a Kenyan tea company dedicated to sourcing, blending, and packing the finest teas from our nation's renowned highlands.</p>
            <div class="social-links-light mt-3 d-flex justify-content-center justify-content-md-start">
              <a href="#" class="social-link-light" v-for="social in socials" :key="social.icon">
                <i :class="social.icon"></i>
              </a>
            </div>
          </div>
          <div class="col-md-3 text-center text-md-start">
            <h5 class="footer-title-light mb-3">Quick Links</h5>
            <ul class="footer-links-light">
              <li v-for="link in exploreLinks" :key="link">
                <a href="#" @click.prevent="scrollToSection(link.toLowerCase())">{{ link }}</a>
              </li>
            </ul>
          </div>
          <div class="col-md-4 text-center text-md-start">
            <h5 class="footer-title-light mb-3">Connect With Us</h5>
            <ul class="footer-contact-light">
              <li><i class="fas fa-envelope text-gold me-2"></i> info@chaizentea.com</li>
              <li><i class="fas fa-phone-alt text-gold me-2"></i> +254 700 824 000</li>
              <li><i class="fas fa-map-marker-alt text-gold me-2"></i> Mombasa, Kenya</li>
            </ul>
          </div>
        </div>
        <div class="footer-bottom-light">
          <div class="footer-line-light my-4"></div>
          <p class="text-center small opacity-50">© 2026 Chaizen — Premium Kenyan Tea Exporters. All rights reserved.</p>
        </div>
      </div>
    </footer>

    <!-- Modal -->
    <div v-if="showInquiry" class="modal-light" @click.self="showInquiry = false">
      <div class="modal-content-light">
        <div class="modal-header-light">
          <h3><i class="fas fa-leaf me-2 text-gold"></i>Request Free Samples</h3>
          <button class="modal-close-light" @click="showInquiry = false">×</button>
        </div>
        <div class="modal-body-light">
          <input type="text" placeholder="Your name" class="form-control-light mb-3">
          <input type="email" placeholder="Email address" class="form-control-light mb-3">
          <input type="text" placeholder="Company name" class="form-control-light mb-3">
          <select class="form-control-light mb-3">
            <option>Select tea type</option>
            <option>Black CTC Teas</option>
            <option>Orthodox Teas</option>
            <option>Green & Specialty Teas</option>
            <option>Purple Tea</option>
            <option>Value-Added Products</option>
          </select>
          <textarea placeholder="Tell us about your requirements..." class="form-control-light mb-3" rows="3"></textarea>
          <button class="btn-submit-light w-100" @click="showInquiry = false">
            Submit Request
            <i class="fas fa-paper-plane ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ChaizenLandingTea',
  data() {
    return {
      scrolled: false,
      currentTestimonial: 0,
      currentSlide: 0,
      showInquiry: false,
      cursorX: 0,
      cursorY: 0,
      heroImage: require('@/assets/chaizen.jpeg'),
      stats: [
        { value: 75, label: 'Countries Served', displayValue: '75', suffix: '+' },
        { value: 10, label: 'Tonnes Exported', displayValue: '10k', suffix: '+' },
        { value: 20, label: 'Years Experience', displayValue: '20', suffix: '+' }
      ],
      trustStats: [
        { icon: 'fas fa-globe-asia', valueNum: 75, label: 'Countries Served', displayNum: '75', suffix: '+' },
        { icon: 'fas fa-certificate', valueNum: 100, label: 'Quality Guaranteed', displayNum: '100', suffix: '%' },
        { icon: 'fas fa-truck', valueNum: 10, label: 'Tonnes Exported', displayNum: '10k', suffix: '+' },
        { icon: 'fas fa-award', valueNum: 20, label: 'Years Experience', displayNum: '20', suffix: '+' }
      ],
      values: ['Quality', 'Honesty', 'Sustainability', 'Alliance'],
      teaProducts: [
        { title: 'Black CTC Teas', description: '6 premium CTC grades ideal for tea bags, blending, and everyday drinking.', icon: 'fas fa-mug-hot' },
        { title: 'Orthodox Teas', description: '6 whole-leaf orthodox grades with nuanced flavour profiles for specialty markets.', icon: 'fas fa-leaf' },
        { title: 'Green & Specialty', description: 'Kenyan green teas and rare TRFK Purple Tea cultivar for health-conscious consumers.', icon: 'fas fa-seedling' },
        { title: 'Value-Added Products', description: 'Tea bags, retail cartons, and private label packaging solutions.', icon: 'fas fa-boxes' }
      ],
      carouselItems: [
  { 
   name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch1.jpeg') // For local images
   
  },
    { 
   name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch2.jpeg') // For local images
   
  },
    { 
   name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch3.jpeg') // For local images
   
  },
    { 
   name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch4.jpeg') // For local images
   
  },
    { 
    name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch5.jpeg') // For local images
   
  },
    { 
    name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch6.jpeg') // For local images
   
  },
    { 
    name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch7.jpeg') // For local images
   
  },
    { 
  name: 'Black CTC PF', 
    description: 'Kenyan tea quality',
    tag: '',
    image: require('@/assets/ch8.jpeg') // For local images
   
  },
 
  // Add more items...
],
      qualityFeatures: [
        { title: 'Food Safety', description: 'HACCP & ISO 22000 certified processes', icon: 'fas fa-shield-alt' },
        { title: 'Full Traceability', description: 'Garden-to-cup tracking on every shipment', icon: 'fas fa-chart-line' },
        { title: 'Expert Tasting', description: 'Rigorous cupping by certified tasters', icon: 'fas fa-mug-hot' },
        { title: 'Reliable Supply', description: 'Consistent quality across every order', icon: 'fas fa-handshake' }
      ],
      exportCountries: [
        { code: 'YE', name: 'Yemen', flag: '🇾🇪' },
        { code: 'GB', name: 'UK', flag: '🇬🇧' },
        { code: 'AE', name: 'UAE', flag: '🇦🇪' },
        { code: 'TR', name: 'Turkey', flag: '🇹🇷' },
        { code: 'SD', name: 'Sudan', flag: '🇸🇩' },
        { code: 'SO', name: 'Somalia', flag: '🇸🇴' },
        { code: 'PK', name: 'Pakistan', flag: '🇵🇰' },
        { code: 'OM', name: 'Oman', flag: '🇴🇲' },
        { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿' },
        { code: 'IR', name: 'Iran', flag: '🇮🇷' },
        { code: 'EG', name: 'Egypt', flag: '🇪🇬' },
        { code: 'AF', name: 'Afghanistan', flag: '🇦🇫' },
        { code: 'RU', name: 'Russia', flag: '🇷🇺' },
        { code: 'IN', name: 'India', flag: '🇮🇳' },
        { code: 'PL', name: 'Poland', flag: '🇵🇱' },
        { code: 'DE', name: 'Germany', flag: '🇩🇪' },
        { code: 'ES', name: 'Spain', flag: '🇪🇸' },
        { code: 'CN', name: 'China', flag: '🇨🇳' }
      ],
      testimonials: [
        { name: 'Elizabeth Lesutu', title: 'Business Woman', quote: 'I cannot possibly emphasise my satisfaction in doing business with Chaizen. You will always be my preferred Bulk Tea Trader!' },
        { name: 'Andrew Managu', title: 'Retailer', quote: 'I have been buying tea from these guys every week for 5 years now! Never been disappointed.' },
        { name: 'James Kimani', title: 'Distributor', quote: 'The quality and consistency of Chaizen teas are exceptional. A truly reliable partner for our business.' }
      ],
      affiliations: [
        'Kenya Trade Network Agency', 'Kenya National Chamber of Commerce', 'Tea Board of Kenya',
        'Kenya Ports Authority', 'Kenya Bureau of Standards', 'Kenya Maritime Authority',
        'National Environment Management Authority', 'SGS Certification', 'HACCP Certified', 'ISO 22000'
      ],
      navItems: ['Home', 'About', 'Products', 'Quality', 'Global', 'Stories'],
      socials: [
        { icon: 'fab fa-linkedin-in' },
        { icon: 'fab fa-instagram' },
        { icon: 'fab fa-x-twitter' },
        { icon: 'fab fa-facebook-f' }
      ],
      exploreLinks: ['Home', 'About', 'Products', 'Quality', 'Global', 'Stories', 'Contact']
    }
  },
  computed: {
    cursorStyle() {
      return {
        transform: `translate(${this.cursorX}px, ${this.cursorY}px)`
      }
    },
    carouselPages() {
      return Math.ceil(this.carouselItems.length / 3)
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    window.addEventListener('mousemove', this.handleMouseMove)
    this.initCounters()
    this.initScrollReveal()
    this.startAutoSlide()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('mousemove', this.handleMouseMove)
    clearInterval(this.autoSlideInterval)
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 50
    },
    handleMouseMove(e) {
      this.cursorX = e.clientX - 20
      this.cursorY = e.clientY - 20
    },
    handleCardMouseMove(e) {
      const card = e.currentTarget
      const rect = card.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const centerX = rect.width / 2
      const centerY = rect.height / 2
      const rotateX = (y - centerY) / 15
      const rotateY = (centerX - x) / 15
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-10px)`
    },
    handleCardMouseLeave(e) {
      e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)'
    },
    startTiltAnimation(e) {
      const imgContainer = e.currentTarget.parentElement
      imgContainer.style.animation = 'none'
      imgContainer.style.transform = 'scale(1.02)'
      setTimeout(() => {
        imgContainer.style.animation = 'float3DLight 4s infinite ease-in-out'
        imgContainer.style.transform = 'scale(1)'
      }, 200)
    },
    resetTiltAnimation(e) {
      const imgContainer = e.currentTarget.parentElement
      imgContainer.style.transform = 'scale(1)'
    },
    startAutoSlide() {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide()
      }, 5000)
    },
    nextSlide() {
      const maxSlides = Math.ceil(this.carouselItems.length / 3) - 1
      this.currentSlide = this.currentSlide >= maxSlides ? 0 : this.currentSlide + 1
    },
    prevSlide() {
      const maxSlides = Math.ceil(this.carouselItems.length / 3) - 1
      this.currentSlide = this.currentSlide <= 0 ? maxSlides : this.currentSlide - 1
    },
    goToSlide(index) {
      this.currentSlide = index
    },
    initCounters() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const counters = entry.target.querySelectorAll('.counter')
            counters.forEach(counter => {
              const target = parseInt(counter.dataset.target)
              let current = 0
              const increment = target / 50
              const updateCounter = () => {
                if (current < target) {
                  current += increment
                  counter.textContent = Math.ceil(current)
                  setTimeout(updateCounter, 20)
                } else {
                  counter.textContent = target
                }
              }
              updateCounter()
            })
            observer.unobserve(entry.target)
          }
        })
      })
      document.querySelectorAll('.stats-section-light, .stats-container-light').forEach(el => observer.observe(el))
    },
    initScrollReveal() {
      const elements = document.querySelectorAll('[data-aos]')
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate')
            revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.1 })
      elements.forEach(el => revealObserver.observe(el))
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId === 'home' ? 'home' : sectionId)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    nextTestimonial() {
      this.currentTestimonial = (this.currentTestimonial + 1) % this.testimonials.length
    },
    prevTestimonial() {
      this.currentTestimonial = (this.currentTestimonial - 1 + this.testimonials.length) % this.testimonials.length
    }
  }
}
</script>

<style scoped>
/* ========== ALL EXISTING STYLES FROM YOUR PROVIDED CODE ========== */
/* Keeping every style exactly as you had them - no design changes */

@import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&family=Playfair+Display:wght@400;500;600;700;800;900&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
}

.chaizen-app {
  font-family: 'Inter', sans-serif;
  background: linear-gradient(135deg, #fff9f5 0%, #fff5ed 100%);
  color: #2d2a24;
  overflow-x: hidden;
  position: relative;
  width: 100%;
  max-width: 100%;
}

/* Force center alignment for all containers */
.container,
.container-fluid {
  margin-left: auto !important;
  margin-right: auto !important;
}

/* Custom Cursor */
.cursor-follower {
  position: fixed;
  width: 30px;
  height: 30px;
  border: 2px solid #c8a45e;
  border-radius: 50%;
  pointer-events: none;
  z-index: 9999;
  transition: transform 0.05s ease;
  backdrop-filter: blur(4px);
  background: rgba(200, 164, 94, 0.1);
  animation: cursorPulseLight 2s infinite;
}

@keyframes cursorPulseLight {
  0%, 100% { transform: scale(1); opacity: 0.4; }
  50% { transform: scale(1.3); opacity: 0.8; }
}

/* Glass Navigation */
.glass-nav-light {
  background: rgba(255, 250, 245, 0.92);
  backdrop-filter: blur(12px);
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
  width: 100%;
}

.glass-nav-light.scrolled {
  background: rgba(255, 250, 245, 0.98);
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.08);
}

.navbar-brand {
  font-family: 'Playfair Display', serif;
  font-size: 1.6rem;
  font-weight: 800;
  background: linear-gradient(135deg, #c8a45e, #e8c48a);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  position: relative;
  text-decoration: none;
}

.brand-dot-light {
  position: absolute;
  width: 5px;
  height: 5px;
  background: #c8a45e;
  border-radius: 50%;
  bottom: 6px;
  right: -8px;
  animation: pulseLight 1.5s infinite;
}

@keyframes pulseLight {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.3); }
}

.nav-link-light {
  position: relative;
  color: #4a3f32 !important;
  font-weight: 500;
  font-size: 0.9rem;
  text-decoration: none;
  transition: color 0.3s;
  cursor: pointer;
}

.nav-hover-line-light {
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #c8a45e, #e8c48a);
  transition: width 0.3s;
}

.nav-link-light:hover .nav-hover-line-light {
  width: 100%;
}

.btn-chaizen-light {
  background: linear-gradient(105deg, #c8a45e 0%, #b8944f 100%);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.5rem 1.2rem;
  border-radius: 60px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(200, 164, 94, 0.3);
  text-decoration: none;
  display: inline-block;
}

.btn-chaizen-light:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(200, 164, 94, 0.4);
  color: white;
}

/* Hero Section */
.hero-section-light {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #fff5ed 0%, #fffaf5 100%);
  width: 100%;
}

.hero-bg-animation-light {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.orb-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.2;
  animation: floatLight 20s infinite ease-in-out;
}

.orb-1-light {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #f5e6d3, #e8d5bc);
  top: -150px;
  right: -80px;
}

.orb-2-light {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, #ffe8d6, #f0dcc9);
  bottom: -80px;
  left: -80px;
  animation-delay: 5s;
}

.orb-3-light {
  width: 250px;
  height: 250px;
  background: radial-gradient(circle, #fdf0e3, #fae6d4);
  top: 50%;
  left: 50%;
  animation-delay: 10s;
  transform: translate(-50%, -50%);
}

@keyframes floatLight {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, -20px) scale(1.05); }
}

.hero-title-light {
  font-size: 2.8rem;
  line-height: 1.2;
  font-weight: 700;
}

.text-gradient-light {
  background: linear-gradient(135deg, #c8a45e, #dbb87a);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-subtitle-light {
  color: #5a4f42;
  font-size: 1rem;
  line-height: 1.5;
}

.badge-glow-light {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 1rem;
  background: rgba(200, 164, 94, 0.12);
  border: 1px solid rgba(200, 164, 94, 0.3);
  border-radius: 60px;
  backdrop-filter: blur(10px);
  font-weight: 500;
  font-size: 0.75rem;
  color: #8b6b3c;
}

.btn-3d-light {
  position: relative;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
}

.btn-3d-front-light {
  display: inline-block;
  padding: 0.6rem 1.5rem;
  background: linear-gradient(135deg, #c8a45e, #b08a42);
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 60px;
  transition: transform 0.2s;
  box-shadow: 0 8px 20px rgba(200, 164, 94, 0.35);
}

.btn-3d-light:hover .btn-3d-front-light {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(200, 164, 94, 0.45);
}

.btn-outline-premium-light {
  padding: 0.6rem 1.5rem;
  background: transparent;
  border: 2px solid #c8a45e;
  color: #c8a45e;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 60px;
  transition: all 0.3s;
}

.btn-outline-premium-light:hover {
  background: #c8a45e;
  color: white;
  transform: translateY(-2px);
}

.stat-card-light {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 0.8rem;
  text-align: center;
  border: 1px solid rgba(200, 164, 94, 0.2);
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.stat-card-light:hover {
  transform: translateY(-5px);
  border-color: #c8a45e;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.85);
}

.stat-number-light {
  font-size: 1.6rem;
  font-weight: 800;
  color: #c8a45e;
  font-family: 'Playfair Display', serif;
}

.stat-label-light {
  font-size: 0.7rem;
  color: #6b5e4e;
  margin-bottom: 0;
}

.stat-shine-light {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.5s;
}

.stat-card-light:hover .stat-shine-light {
  left: 100%;
}

.hero-3d-card-light {
  position: relative;
  perspective: 1000px;
  max-width: 400px;
  margin: 0 auto;
}

.hero-3d-inner-light {
  position: relative;
  transform-style: preserve-3d;
  animation: float3DLight 4s infinite ease-in-out;
  transition: transform 0.3s ease;
}

@keyframes float3DLight {
  0%, 100% { transform: translateY(0) rotateY(0deg) rotateX(0deg); }
  25% { transform: translateY(-8px) rotateY(3deg) rotateX(2deg); }
  75% { transform: translateY(5px) rotateY(-2deg) rotateX(-1deg); }
}

.hero-3d-img-light {
  border-radius: 1.5rem;
  box-shadow: 0 20px 35px rgba(0, 0, 0, 0.12);
  width: 100%;
  max-width: 100%;
  height: auto;
  transition: all 0.3s ease;
  cursor: pointer;
}

.hero-3d-img-light:hover {
  transform: scale(1.02);
  box-shadow: 0 25px 45px rgba(0, 0, 0, 0.15);
}

.glow-effect-light {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 1.5rem;
  background: radial-gradient(circle at 50% 50%, rgba(200, 164, 94, 0.15), transparent);
  pointer-events: none;
  animation: glowPulse 3s infinite;
}

@keyframes glowPulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.floating-particles-light {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.particle-light {
  position: absolute;
  width: 4px;
  height: 4px;
  background: #c8a45e;
  border-radius: 50%;
  opacity: 0.5;
  animation: particleFloatEnhanced 4s infinite ease-in-out;
  box-shadow: 0 0 4px rgba(200, 164, 94, 0.5);
}

@keyframes particleFloatEnhanced {
  0% { transform: translate(0, 0) scale(1); opacity: 0.5; }
  50% { transform: translate(15px, -20px) scale(1.2); opacity: 0.3; }
  100% { transform: translate(30px, -40px) scale(0); opacity: 0; }
}

.scroll-indicator-light {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  cursor: pointer;
  animation: bounceLight 2s infinite;
}

@keyframes bounceLight {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(5px); }
}

.scroll-text-light {
  font-size: 0.6rem;
  letter-spacing: 2px;
  opacity: 0.6;
  color: #8b7355;
}

.scroll-wheel-light {
  width: 18px;
  height: 28px;
  border: 2px solid rgba(139, 115, 85, 0.3);
  border-radius: 20px;
  margin-top: 4px;
  position: relative;
  margin-left: auto;
  margin-right: auto;
}

.scroll-wheel-light::before {
  content: '';
  position: absolute;
  top: 5px;
  left: 50%;
  transform: translateX(-50%);
  width: 3px;
  height: 6px;
  background: #c8a45e;
  border-radius: 2px;
  animation: scrollWheelLight 1.5s infinite;
}

@keyframes scrollWheelLight {
  0% { opacity: 1; transform: translateX(-50%) translateY(0); }
  100% { opacity: 0; transform: translateX(-50%) translateY(8px); }
}

/* Stats Section */
.stats-section-light {
  background: linear-gradient(135deg, #fffaf5 0%, #fff5ed 100%);
  position: relative;
}

.trust-card-light {
  background: white;
  border-radius: 1rem;
  padding: 1rem;
  text-align: center;
  border: 1px solid rgba(200, 164, 94, 0.15);
  transition: all 0.3s;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.03);
}

.trust-card-light:hover {
  transform: translateY(-5px);
  border-color: #c8a45e;
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.08);
}

.trust-icon-wrapper-light {
  position: relative;
  display: inline-block;
  margin-bottom: 0.5rem;
}

.trust-icon-light {
  font-size: 1.6rem;
  color: #c8a45e;
  position: relative;
  z-index: 2;
}

.icon-ring-light {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 45px;
  height: 45px;
  border: 2px solid rgba(200, 164, 94, 0.2);
  border-radius: 50%;
  animation: ringPulseLight 2s infinite;
}

@keyframes ringPulseLight {
  0% { width: 45px; height: 45px; opacity: 0.4; }
  100% { width: 60px; height: 60px; opacity: 0; }
}

.trust-number-light {
  font-size: 1.6rem;
  font-weight: 800;
  color: #c8a45e;
  margin-bottom: 0.2rem;
}

.trust-label-light {
  color: #8b7355;
  font-size: 0.7rem;
  margin-bottom: 0;
}

/* About Section Styles */
.mission-vision-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.mission-card, .vision-card {
  background: rgba(200, 164, 94, 0.05);
  border-radius: 1rem;
  padding: 1rem;
  text-align: center;
  border: 1px solid rgba(200, 164, 94, 0.1);
  transition: all 0.3s;
}

.mission-card:hover, .vision-card:hover {
  transform: translateY(-3px);
  border-color: #c8a45e;
  background: rgba(200, 164, 94, 0.08);
}

.mission-icon, .vision-icon {
  font-size: 1.8rem;
  color: #c8a45e;
  margin-bottom: 0.5rem;
}

.mission-card h4, .vision-card h4 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.3rem;
  color: #2d2a24;
}

.mission-card p, .vision-card p {
  font-size: 0.75rem;
  color: #6b5e4e;
  margin-bottom: 0;
}

.values-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.8rem;
  margin-top: 1rem;
}

.value-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(200, 164, 94, 0.08);
  padding: 0.3rem 0.8rem;
  border-radius: 60px;
  font-size: 0.7rem;
  font-weight: 500;
  color: #5a4f42;
}

.value-icon {
  font-size: 0.7rem;
  color: #c8a45e;
}

/* Quality Section */
.quality-section-light {
  background: #ffffff;
}

.quality-features-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.quality-feature {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  background: rgba(200, 164, 94, 0.03);
  padding: 0.8rem;
  border-radius: 0.8rem;
  transition: all 0.3s;
}

.quality-feature:hover {
  background: rgba(200, 164, 94, 0.08);
  transform: translateX(3px);
}

.quality-feature-icon {
  width: 40px;
  height: 40px;
  background: rgba(200, 164, 94, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c8a45e;
  font-size: 1.2rem;
}

.quality-feature-content h4 {
  font-size: 0.85rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
  color: #2d2a24;
}

.quality-feature-content p {
  font-size: 0.7rem;
  color: #6b5e4e;
  margin-bottom: 0;
}

/* Services Section */
.services-section-light {
  background: #ffffff;
}

.section-badge-light {
  display: inline-block;
  padding: 0.4rem 1rem;
  background: rgba(200, 164, 94, 0.1);
  border-radius: 60px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #c8a45e;
}

.section-divider-light {
  width: 50px;
  height: 2px;
  background: linear-gradient(90deg, #c8a45e, transparent);
  margin: 0.8rem auto 0;
}

.service-card-light {
  perspective: 1000px;
  cursor: pointer;
  transition: transform 0.3s;
}

.service-card-inner-light {
  background: white;
  border-radius: 1rem;
  padding: 1.2rem;
  border: 1px solid #f0e6dc;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  height: 100%;
}

.service-card-inner-light:hover {
  border-color: #c8a45e;
  box-shadow: 0 15px 35px rgba(200, 164, 94, 0.12);
}

.service-icon-wrapper-light {
  position: relative;
  width: 50px;
  height: 50px;
  margin-bottom: 0.8rem;
}

.service-icon-bg-light {
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(200, 164, 94, 0.12), transparent);
  border-radius: 50%;
  animation: pulseLight 2s infinite;
}

.service-icon-light {
  position: relative;
  font-size: 1.5rem;
  color: #c8a45e;
  z-index: 2;
}

.service-title-light {
  font-weight: 700;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: #2d2a24;
}

.service-desc-light {
  color: #8b7355;
  font-size: 0.75rem;
  line-height: 1.4;
}

.service-shimmer-light {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(200, 164, 94, 0.05), transparent);
  transition: left 0.5s;
}

.service-card-inner-light:hover .service-shimmer-light {
  left: 100%;
}

/* About Section base styles */
.about-section-light {
  background: #ffffff;
}

.about-image-stack-light {
  position: relative;
  text-align: center;
}

.image-main-light {
  position: relative;
  display: inline-block;
}

.about-img-light {
  border-radius: 1rem;
  width: 100%;
  max-width: 400px;
  transition: transform 0.3s;
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.05);
}

.about-text-light {
  color: #6b5e4e;
  line-height: 1.5;
  font-size: 0.85rem;
}

.about-lead-light {
  font-size: 0.9rem;
  font-weight: 500;
  color: #4a3f32;
}

.image-glow-light {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  background: radial-gradient(circle at 30% 30%, rgba(200, 164, 94, 0.1), transparent);
  pointer-events: none;
}

.image-decoration-light {
  position: absolute;
  bottom: -15px;
  right: -15px;
  width: 100px;
  height: 100px;
  border: 2px solid #c8a45e;
  border-radius: 50%;
  z-index: 1;
  opacity: 0.3;
}

.btn-premium-light {
  position: relative;
  padding: 0.6rem 1.5rem;
  background: linear-gradient(135deg, #c8a45e, #b08a42);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 60px;
  overflow: hidden;
  transition: transform 0.3s;
}

.btn-premium-light:hover {
  transform: translateY(-2px);
}

.btn-shine-light {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.btn-premium-light:hover .btn-shine-light {
  left: 100%;
}

/* Global Section */
.global-section-light {
  background: linear-gradient(135deg, #fdf8f3 0%, #faf3eb 100%);
}

.country-pill-light {
  background: white;
  border: 1px solid #e8dccc;
  padding: 0.3rem 1rem;
  border-radius: 60px;
  font-size: 0.7rem;
  transition: all 0.3s;
  cursor: pointer;
  color: #5a4f42;
  display: inline-block;
}

.country-pill-light:hover {
  background: #c8a45e;
  color: white;
  transform: translateY(-2px);
  border-color: #c8a45e;
}

/* Testimonials */
.testimonials-section-light {
  background: linear-gradient(135deg, #fdf8f3 0%, #faf3eb 100%);
}

.testimonials-slider-light {
  position: relative;
  overflow: hidden;
}

.testimonial-track-light {
  display: flex;
  transition: transform 0.5s ease;
}

.testimonial-card-light {
  flex: 0 0 33.33%;
  padding: 0.8rem;
}

.testimonial-content-light {
  background: white;
  border-radius: 1rem;
  padding: 1.2rem;
  border: 1px solid #f0e6dc;
  transition: all 0.3s;
  position: relative;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
}

.testimonial-content-light:hover {
  transform: translateY(-5px);
  border-color: #c8a45e;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
}

.quote-icon-light {
  position: absolute;
  top: 10px;
  right: 15px;
  font-size: 1.5rem;
  opacity: 0.08;
  color: #c8a45e;
}

.testimonial-text-light {
  font-size: 0.8rem;
  line-height: 1.5;
  margin-bottom: 0.8rem;
  color: #5a4f42;
}

.author-avatar-light {
  width: 35px;
  height: 35px;
  background: #c8a45e;
  border-radius: 50%;
  margin-right: 0.8rem;
  overflow: hidden;
}

.avatar-inner-light {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #c8a45e, #dbb87a);
}

.author-info-light strong {
  font-size: 0.8rem;
}

.author-info-light small {
  font-size: 0.65rem;
}

.rating-light {
  margin-top: 0.5rem;
  color: #c8a45e;
  font-size: 0.7rem;
}

.slider-controls-light {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
  margin-top: 1rem;
}

.slider-btn-light {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: white;
  border: 1px solid #e0d4c8;
  color: #c8a45e;
  transition: all 0.3s;
  cursor: pointer;
}

.slider-btn-light:hover {
  background: #c8a45e;
  color: white;
  transform: scale(1.05);
}

.slider-dots-light {
  display: flex;
  gap: 0.4rem;
}

.dot-light {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e0d4c8;
  cursor: pointer;
  transition: all 0.3s;
}

.dot-light.active {
  width: 18px;
  background: #c8a45e;
}

/* CTA Section */
.cta-section-light {
  background: #ffffff;
}

.cta-card-light {
  background: linear-gradient(135deg, #fdf8f3 0%, #faf3eb 100%);
  border-radius: 1rem;
  padding: 1.5rem;
  border: 1px solid #f0e6dc;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
}

.cta-card-light:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.05);
}

.btn-cta-light {
  position: relative;
  padding: 0.6rem 1.5rem;
  background: linear-gradient(135deg, #c8a45e, #b08a42);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 60px;
  overflow: hidden;
  transition: all 0.3s;
}

.btn-cta-light:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 20px rgba(200, 164, 94, 0.3);
}

/* Affiliations */
.affiliation-pill {
  background: white;
  border: 1px solid #e8dccc;
  padding: 0.3rem 0.8rem;
  border-radius: 60px;
  font-size: 0.65rem;
  color: #6b5e4e;
}

/* Footer */
.footer-light {
  background: #2d2a24;
  color: #e0d8cf;
}

.footer-logo-light {
  font-family: 'Playfair Display', serif;
  font-size: 1.4rem;
  font-weight: 800;
  color: white;
}

.footer-desc-light {
  color: #b8ae9f;
  font-size: 0.75rem;
}

.footer-links-light {
  list-style: none;
  padding: 0;
}

.footer-links-light li a {
  color: #b8ae9f;
  text-decoration: none;
  transition: color 0.3s;
  font-size: 0.75rem;
}

.footer-links-light li a:hover {
  color: #c8a45e;
}

.footer-contact-light {
  list-style: none;
  padding: 0;
}

.footer-contact-light li {
  margin-bottom: 0.4rem;
  color: #b8ae9f;
  font-size: 0.7rem;
}

.social-link-light {
  position: relative;
  display: inline-flex;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(200, 164, 94, 0.15);
  align-items: center;
  justify-content: center;
  color: #c8a45e;
  margin-right: 0.5rem;
  transition: all 0.3s;
  text-decoration: none;
  font-size: 0.8rem;
}

.social-link-light:hover {
  background: #c8a45e;
  color: #2d2a24;
  transform: translateY(-3px);
}

.footer-line-light {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(200, 164, 94, 0.3), transparent);
  margin: 1rem 0 0.8rem;
}

/* Modal */
.modal-light {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeInLight 0.3s;
}

@keyframes fadeInLight {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-content-light {
  background: white;
  border-radius: 1rem;
  max-width: 450px;
  width: 90%;
  overflow: hidden;
  animation: slideUpLight 0.4s;
}

@keyframes slideUpLight {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header-light {
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #f0e6dc;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header-light h3 {
  font-size: 1.2rem;
}

.modal-close-light {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #c8a45e;
}

.modal-body-light {
  padding: 1.2rem;
}

.form-control-light {
  width: 100%;
  padding: 0.5rem 0.8rem;
  background: #fefaf5;
  border: 1px solid #e8dccc;
  border-radius: 0.6rem;
  color: #2d2a24;
  transition: border-color 0.3s;
  font-size: 0.8rem;
}

.form-control-light:focus {
  outline: none;
  border-color: #c8a45e;
}

select.form-control-light {
  cursor: pointer;
}

.btn-submit-light {
  padding: 0.6rem;
  background: linear-gradient(135deg, #c8a45e, #b08a42);
  border: none;
  color: white;
  font-weight: 600;
  font-size: 0.85rem;
  border-radius: 60px;
  transition: transform 0.3s;
}

.btn-submit-light:hover {
  transform: translateY(-2px);
}

/* ========== NEW CAROUSEL STYLES ========== */
.carousel-section-light {
  background: #ffffff;
}

.carousel-wrapper {
  position: relative;
  overflow: hidden;
  padding: 0 20px;
}

.carousel-container {
  position: relative;
  overflow: hidden;
  border-radius: 1rem;
}

.carousel-track {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.carousel-slide {
  flex: 0 0 33.33%;
  padding: 10px;
}

.carousel-card {
  perspective: 1000px;
  cursor: pointer;
  transition: transform 0.3s;
  height: 100%;
}

.carousel-card-inner {
  background: white;
  border-radius: 1rem;
  overflow: hidden;
  border: 1px solid #f0e6dc;
  transition: all 0.3s;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  height: 100%;
}

.carousel-card-inner:hover {
  border-color: #c8a45e;
  box-shadow: 0 15px 35px rgba(200, 164, 94, 0.12);
  transform: translateY(-5px);
}

.carousel-image-wrapper {
  position: relative;
  overflow: hidden;
  height: 200px;
}

.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.carousel-card-inner:hover .carousel-image {
  transform: scale(1.05);
}

.carousel-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(200, 164, 94, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.carousel-card-inner:hover .carousel-image-overlay {
  opacity: 1;
}

.carousel-image-overlay i {
  font-size: 2rem;
  color: white;
  background: rgba(0, 0, 0, 0.4);
  padding: 15px;
  border-radius: 50%;
}

.carousel-card-content {
  padding: 1rem;
}

.carousel-product-name {
  font-weight: 700;
  font-size: 1rem;
  color: #2d2a24;
  margin-bottom: 0.3rem;
}

.carousel-product-desc {
  font-size: 0.75rem;
  color: #8b7355;
  margin-bottom: 0.5rem;
}

.carousel-product-tag {
  display: inline-block;
  padding: 0.2rem 0.8rem;
  background: rgba(200, 164, 94, 0.12);
  border-radius: 60px;
  font-size: 0.6rem;
  font-weight: 600;
  color: #c8a45e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.carousel-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 1px solid #f0e6dc;
  color: #c8a45e;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.carousel-btn:hover {
  background: #c8a45e;
  color: white;
  transform: translateY(-50%) scale(1.05);
  box-shadow: 0 8px 25px rgba(200, 164, 94, 0.3);
}

.prev-btn {
  left: 0;
}

.next-btn {
  right: 0;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
}

.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0d4c8;
  cursor: pointer;
  transition: all 0.3s;
}

.carousel-dot.active {
  width: 28px;
  background: #c8a45e;
  border-radius: 10px;
}

.carousel-dot:hover {
  background: #c8a45e;
}

/* AOS Animations */
[data-aos] {
  opacity: 0;
  transition: all 0.8s ease;
}

[data-aos="fade-right"] {
  transform: translateX(-30px);
}

[data-aos="fade-left"] {
  transform: translateX(30px);
}

[data-aos="fade-up"] {
  transform: translateY(20px);
}

[data-aos="zoom-in"] {
  transform: scale(0.95);
}

[data-aos="flip-left"] {
  transform: rotateY(-90deg);
}

[data-aos="flip-up"] {
  transform: rotateX(-90deg);
}

.aos-animate {
  opacity: 1 !important;
  transform: translateX(0) translateY(0) scale(1) rotate(0) !important;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title-light {
    font-size: 2rem;
  }
  
  .testimonial-card-light {
    flex: 0 0 100%;
  }
  
  .feature-grid-light {
    grid-template-columns: 1fr;
  }
  
  .cursor-follower {
    display: none;
  }
  
  .orb-light {
    opacity: 0.1;
  }
  
  .hero-3d-img-light {
    max-width: 280px;
  }
  
  .hero-3d-card-light {
    max-width: 100%;
  }
  
  .about-img-light {
    max-width: 100%;
  }

  .mission-vision-grid {
    grid-template-columns: 1fr;
  }

  .quality-features-grid {
    grid-template-columns: 1fr;
  }

  .values-row {
    flex-wrap: wrap;
  }

  /* Carousel responsive */
  .carousel-slide {
    flex: 0 0 100%;
  }

  .carousel-btn {
    width: 30px;
    height: 30px;
    font-size: 0.8rem;
  }

  .carousel-image-wrapper {
    height: 150px;
  }

  .carousel-wrapper {
    padding: 0 10px;
  }
}

@media (min-width: 769px) and (max-width: 991px) {
  .carousel-slide {
    flex: 0 0 50%;
  }
}

@media (min-width: 992px) {
  .hero-3d-card-light {
    margin: 0 auto;
  }
  
  .hero-title-light {
    font-size: 3rem;
  }
}
</style>